// Smart Intake Engine v3.0
// Classifies free-text user needs → 5 fields + WA message

export type SmartIntakeRules = {
  version: string;
  enabled: boolean;
  waMessageTemplate: string;
  categoryKeywords: { slug: string; keywords: string[] }[];
};

export const DEFAULT_SMART_INTAKE_RULES: SmartIntakeRules = {
  version: '3.0',
  enabled: true,
  // Gender-neutral, no exclamation — aligns with lib/waMessage (the canonical engine).
  waMessageTemplate:
    'שלום {שם_עסק},\nלגבי {צורך_המשתמש}.\nמה העלות ומתי זה אפשרי?\nתודה',
  categoryKeywords: [],
};

const ISRAELI_CITIES = [
  'תל אביב', 'יפו', 'ירושלים', 'חיפה', 'ראשון לציון', 'פתח תקווה',
  'אשדוד', 'נתניה', 'באר שבע', 'בני ברק', 'רמת גן', 'גבעתיים',
  'חולון', 'בת ים', 'הרצליה', 'כפר סבא', 'מודיעין', 'לוד', 'רמלה',
  'הוד השרון', 'גבעת שמואל', 'רמת השרון', 'אשקלון', 'רחובות',
  'בית שמש', 'נהריה', 'עכו', 'טבריה', 'עפולה', 'יבנה', 'קריית גת',
  'דימונה', 'אילת', 'נצרת', 'קריית אתא', 'קריית ביאליק', 'קריית מוצקין',
  'גן יבנה', 'רעננה', 'כפר יונה', 'טירת הכרמל', 'מגדל העמק',
  'קרית שמונה', 'רהט', 'נשר', 'יוקנעם', 'אור יהודה', 'אלעד',
];

const PRICE_PATTERNS = [/כמה עולה/i, /מחיר/i, /עלות/i, /תקציב/i, /₪/, /\d+\s*שקל/i, /תמחור/i, /כמה זה/i];

const TIMING_KEYWORDS: Record<string, string> = {
  'דחוף': 'דחוף', 'מיד': 'דחוף', 'ASAP': 'דחוף', 'עכשיו': 'דחוף',
  'היום': 'היום', 'הבוקר': 'היום', 'הערב': 'היום',
  'מחר': 'מחר',
  'השבוע': 'השבוע', 'שבוע הבא': 'שבוע הבא',
  'בהקדם': 'בהקדם האפשרי',
  'בחודש': 'תוך חודש',
};

// Common intros to strip when extracting the need
const INTRO_RE = /^(?:(?:אני|אנחנו)\s+)?(?:צריך|צריכה|מחפש|מחפשת|רוצה|רוצים|צריכים)\s+(?:(?:ל|ב|מ)מצוא\s+|למצוא\s+)?/i;

export type IntakeResult = {
  categorySlug: string | null;
  categoryName: string | null;
  city: string | null;
  cleanNeed: string | null;
  confidence: number;
  priceHint: string | null;
  timingHint: string | null;
};

type CategoryHint = { slug: string; name_he: string; icon?: string };

export function extractCity(text: string): string | null {
  const sorted = [...ISRAELI_CITIES].sort((a, b) => b.length - a.length);
  for (const city of sorted) {
    if (text.includes(city)) return city;
  }
  return null;
}

export function extractPrice(text: string): string | null {
  for (const pat of PRICE_PATTERNS) {
    if (pat.test(text)) {
      const numMatch = text.match(/[\d,]+\s*(?:שקל|₪)/);
      if (numMatch) return numMatch[0].trim();
      return 'ציין בשאלה';
    }
  }
  return null;
}

export function extractTiming(text: string): string | null {
  // Longer phrases first (e.g. "שבוע הבא" before "השבוע")
  const sorted = Object.entries(TIMING_KEYWORDS).sort((a, b) => b[0].length - a[0].length);
  for (const [kw, label] of sorted) {
    if (text.includes(kw)) return label;
  }
  return null;
}

function normalize(s: string): string {
  return s.trim().toLowerCase().replace(/[״"']/g, '').replace(/\s+/g, ' ');
}

function scoreCategory(text: string, cat: CategoryHint, extraKeywords: string[]): number {
  const norm = normalize(text);
  const catName = normalize(cat.name_he);
  let score = 0;

  if (norm.includes(catName)) score += 10;

  const catWords = catName.split(' ').filter(w => w.length > 2);
  const textWords = norm.split(' ');
  for (const cw of catWords) {
    if (textWords.some(tw => tw.includes(cw) || cw.includes(tw))) score += 3;
  }

  for (const kw of extraKeywords) {
    if (norm.includes(normalize(kw))) score += 5;
  }

  return score;
}

// Build a set of word stems to strip from cleanNeed (profession words)
function buildProfessionStems(catName: string): string[] {
  const stems: string[] = [];
  const words = catName.split(' ').filter(w => w.length > 2);
  for (const w of words) {
    stems.push(w);
    // Also try singular-ish form: remove common plural suffixes
    if (w.endsWith('ים')) stems.push(w.slice(0, -2));
    if (w.endsWith('ות')) stems.push(w.slice(0, -2));
    if (w.endsWith('ניקים')) stems.push(w.slice(0, -3));
    if (w.endsWith('ני')) stems.push(w.slice(0, -1) + 'ן');
  }
  return [...new Set(stems)];
}

export function classifyIntake(
  text: string,
  categories: CategoryHint[],
  rules: SmartIntakeRules
): IntakeResult {
  const city = extractCity(text);
  const priceHint = extractPrice(text);
  const timingHint = extractTiming(text);

  let best: CategoryHint | null = null;
  let bestScore = 0;

  for (const cat of categories) {
    const extraKw = rules.categoryKeywords.find(c => c.slug === cat.slug)?.keywords || [];
    const score = scoreCategory(text, cat, extraKw);
    if (score > bestScore) { bestScore = score; best = cat; }
  }

  // ── Extract cleanNeed ────────────────────────────────────────────────────
  let cn = text;

  // 1. Strip city (with common prepositions)
  if (city) {
    cn = cn.replace(new RegExp(`(?:ב|ל|מ|ב)?(?:${city})`, 'g'), '').trim();
  }
  cn = cn.replace(/,?\s*ישראל\s*/g, '').trim();

  // 2. Strip timing keywords (longest first to avoid partial matches)
  const timingSorted = Object.keys(TIMING_KEYWORDS).sort((a, b) => b.length - a.length);
  for (const kw of timingSorted) {
    cn = cn.replace(new RegExp(`(?:ב)?${kw}`, 'g'), '').trim();
  }

  // 3. Strip common intro phrases ("אני צריך", "מחפש" etc.)
  cn = cn.replace(INTRO_RE, '').trim();

  // 4. Strip profession words from matched category (only if confident)
  if (best && bestScore >= 3) {
    const stems = buildProfessionStems(best.name_he);
    for (const stem of stems) {
      if (stem.length < 3) continue;
      // Match with optional leading prepositions and trailing Hebrew suffixes
      const re = new RegExp(`(?:ל|ב|מ|ה|כ|ו|ש)?${stem}(?:ים|ות|ת|י|ן|ית)?(?:\\s|$)`, 'g');
      cn = cn.replace(re, ' ').trim();
    }
  }

  // 5. Strip trailing/leading filler words
  cn = cn.replace(/^(?:ל|ב|ה|ו|כ|מ|של|לבצע|לעשות|עבור|בעניין)\s+/i, '').trim();
  cn = cn.replace(/\s+/g, ' ').trim();
  // Remove stray short fragments (single letters or prepositions)
  cn = cn.split(' ').filter(w => w.length > 1).join(' ').trim();

  // Only return cleanNeed if something meaningful is left
  // (at least 4 chars, and different enough from original text)
  const isNeedMeaningful = cn.length >= 4;

  return {
    categorySlug: bestScore >= 3 ? (best?.slug || null) : null,
    categoryName: bestScore >= 3 ? (best?.name_he || null) : null,
    city,
    cleanNeed: isNeedMeaningful ? cn : null,
    confidence: Math.min(100, bestScore * 10),
    priceHint,
    timingHint,
  };
}

export function buildWaMessage(
  proName: string,
  userNeed: string,
  city: string | null,
  template: string
): string {
  const needWithCity = city ? `${userNeed} ב${city}` : userNeed;
  return template
    .replace(/{שם_עסק}/g, proName)
    .replace(/{צורך_המשתמש}/g, needWithCity)
    .replace(/{עיר}/g, city || '');
}
